import { all, delay, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { CONFIG } from '~/config';

import { getUserFromToken } from '~/modules/helpers';
import ExternalRestClientWithRedirect, { ExternalRestClient } from '~/modules/requests';

import {
  forgotPassword,
  forgotPasswordSuccess,
  login,
  loginFailure,
  loginSuccess,
  logOut,
  logOutSuccess,
  register,
  registerSuccess,
  registerFailure,
  forgotPasswordFailure,
  changePassword,
  changePasswordSuccess,
  changePasswordFailure,
  timeDelay,
  checkUserSessionValidity,
  updateUserProfileSuccess,
  updateUserProfileFailure,
  updateUserProfile,
  deleteAccountSuccess,
  deleteAccountFailure,
  deleteAccount,
  updateUserProfileActionCompleted,
} from '~/actions';
import { LoginParams, ApiResponse, ChangePasswordParams, UserProfileParams } from '~/types';

export function* loginSaga({ payload }: PayloadAction<LoginParams>) {
  try {
    const response: ApiResponse<any> = yield ExternalRestClient.post(
      `${CONFIG.env.apiBaseUrl}v1/user/login`,
      payload
    );

    if (response.status === 200) {
      if (response.data) {
        localStorage.setItem(CONFIG.app.JWTStorageKey, response.data.json.jwt);
        yield put(loginSuccess({
          user: getUserFromToken(response.data.json.jwt),
        }));
      }
    } else {
      yield put(loginFailure({ errorMessage: response.data.error }));
    }
  } catch (error: any) {
    yield put(
      loginFailure(
        {
          errorMessage: error?.response?.data.error || "Request failed. Please try again later."
        }
      ));
  }
}

export function* checkUserSessionValiditySaga() {
  try {
    const response: ApiResponse<any> = yield ExternalRestClient.post(
      `${CONFIG.env.apiBaseUrl}v1/user/session/validity`,
      {}
    );

    if (response.status !== 200) {
      localStorage.removeItem(CONFIG.app.JWTStorageKey);
      yield put(
        logOutSuccess()
      );
      localStorage.removeItem(CONFIG.app.JWTStorageKey);
    }
  } catch (error: any) {
    localStorage.removeItem(CONFIG.app.JWTStorageKey);
    localStorage.removeItem(CONFIG.app.JWTStorageKey);
    yield put(
      logOutSuccess()
    );
  }
}

export function* registerSaga({ payload }: PayloadAction<UserProfileParams>) {
  try {
    const response: ApiResponse<any> = yield ExternalRestClient.post(
      `${CONFIG.env.apiBaseUrl}v1/user/register`,
      payload
    );

    if (response.status === 201) {
      yield put(registerSuccess());
      yield put(login({ ...payload }));
    } else {
      yield put(
        registerFailure(
          {
            errorMessage: `${response.data.error}`,
          }
        ));
    }
  } catch (error: any) {
    yield put(
      registerFailure(
        {
          errorMessage: `${error?.response?.data.error || "Request failed. Please try again later."}`,
        }
      ));
  }

}

export function* updateProfileSaga({ payload }: PayloadAction<LoginParams>) {
  try {
    const response: ApiResponse<any> = yield ExternalRestClientWithRedirect.patch(
      `${CONFIG.env.apiBaseUrl}v1/user/update`,
      payload
    );
    if (response.status >= 200 && response.status < 300) {
      yield put(updateUserProfileSuccess());

    } else {
      yield put(
        updateUserProfileFailure(
          {
            errorMessage: `${response.data.error}`,
          }
        ));
    }
  } catch (error: any) {
    yield put(
      updateUserProfileFailure(
        {
          errorMessage: `${error?.response?.data.error || "Request failed. Please try again later."}`,
        }
      ));
  } finally {
    yield put(updateUserProfileActionCompleted({
      user: getUserFromToken(localStorage.getItem(CONFIG.app.JWTStorageKey) || ''),
    }));
  }
}

export function* deleteAccountSaga() {
  try {
    const response: ApiResponse<any> = yield ExternalRestClientWithRedirect.delete(
      `${CONFIG.env.apiBaseUrl}v1/user/delete`
    );

    if (response.status >= 200 && response.status < 300) {
      yield put(deleteAccountSuccess());
      yield put(logOutSuccess());
      localStorage.removeItem(CONFIG.app.JWTStorageKey);
    } else {
      yield put(
        deleteAccountFailure(
          {
            errorMessage: `${response.data.error}`,
          }
        ));
    }
  } catch (error: any) {
    yield put(
      deleteAccountFailure(
        {
          errorMessage: `${error?.response?.data.error || "Request failed. Please try again later."}`,
        }
      ));
  }

}

export function* timeDelaySaga({ payload }: PayloadAction<number>) {
  delay(payload);
}

export function* forgotPasswordSaga({ payload }: PayloadAction<LoginParams>) {

  try {
    const response: ApiResponse<any> = yield ExternalRestClient.post(
      `${CONFIG.env.apiBaseUrl}v1/user/reset`,
      payload
    );

    if (response.status >= 200 && response.status < 300) {
      yield put(forgotPasswordSuccess());
    } else {
      yield put(
        forgotPasswordFailure(
          {
            errorMessage: `${response.data.error}`,
          }
        ));
    }
  } catch (error: any) {
    yield put(
      forgotPasswordFailure(
        {
          errorMessage: `${error?.response?.data.error || "Request failed. Please try again later."}`,
        }
      ));
  }

}

export function* changePasswordSaga({ payload }: PayloadAction<ChangePasswordParams>) {
  try {
    const response: ApiResponse<any> = yield ExternalRestClient.post(
      `${CONFIG.env.apiBaseUrl}v1/user/reset/password`,
      payload
    );

    if (response.status === 200) {
      yield put(changePasswordSuccess());
    } else if (response.status === 404) {
      changePasswordFailure(
        {
          errorMessage: "Request failed. Reset link expired or invalid."
        }
      );
    } else {
      yield put(changePasswordFailure({ errorMessage: response.data.error }));
    }
  } catch (error: any) {
    if (error?.response?.status == 404) {
      yield put(changePasswordFailure(
        {
          errorMessage: "Request failed. Reset link expired or invalid."
        }
      ));
    } else {
      yield put(
        changePasswordFailure(
          {
            errorMessage: error?.response?.data.error || "Request failed. Please try again later."
          }
        ));
    }
  }
}

export function* logoutSaga() {
  localStorage.removeItem(CONFIG.app.JWTStorageKey);
  yield put(
    logOutSuccess()
  );
}

export default function* root() {
  yield all([
    takeLatest(login.type, loginSaga),
    takeLatest(logOut.type, logoutSaga),
    takeLatest(register.type, registerSaga),
    takeLatest(forgotPassword.type, forgotPasswordSaga),
    takeLatest(changePassword.type, changePasswordSaga),
    takeLatest(timeDelay.type, timeDelaySaga),
    takeLatest(checkUserSessionValidity.type, checkUserSessionValiditySaga),
    takeLatest(updateUserProfile.type, updateProfileSaga),
    takeLatest(deleteAccount.type, deleteAccountSaga),
  ]);
}
